import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Rss, Music2, Activity, Play, Pause, Loader2, Heart, MessageCircle,
  CheckCircle2, XCircle, Clock, Image as ImageIcon, Film, Users, Library,
} from "lucide-react";
import { api } from "@/api/client";
import { socialApi } from "@/api/social";

// Personal Command Centre — the desktop right-pane for EVERY signed-in user
// (distinct from the founder telemetry deck). Three neo-brutalist panels:
//   • top-left  → their Feed
//   • top-right → a Music player (their vault + community shared music)
//   • bottom    → their jobs / generations + status
const PANEL = "border-4 border-[#1A1A1A] bg-[#0E0E0E] shadow-[5px_5px_0px_#000] rounded-2xl overflow-hidden flex flex-col";
const HEAD = "shrink-0 flex items-center gap-2 px-4 py-3 border-b-4 border-[#FFD028]/50 bg-[#141414]";
const HTITLE = "font-mono text-[10px] font-black uppercase tracking-[0.22em] text-[#FFD028]";

const STATUS_UI = {
  completed: { c: "text-emerald-400", I: CheckCircle2, label: "Done" },
  failed: { c: "text-red-400", I: XCircle, label: "Failed" },
  processing: { c: "text-[#FFD028]", I: Loader2, label: "Working", spin: true },
};

function FeedPanel() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    socialApi.myFeed?.(0, 15).then((d) => setItems(d.items || []))
      .catch(() => api.get("/social/myfeed", { params: { offset: 0, limit: 15 } })
        .then((r) => setItems(r.data.items || [])).catch(() => setItems([])));
  }, []);
  return (
    <div className={`${PANEL} col-start-1 row-start-1`} data-testid="cc-feed-panel">
      <div className={HEAD}><Rss size={14} className="text-[#FFD028]" /><span className={HTITLE}>Your Feed</span></div>
      <div className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-2.5">
        {items === null ? (
          <div className="h-full grid place-items-center"><Loader2 className="animate-spin text-[#FFD028]" size={18} /></div>
        ) : items.length === 0 ? (
          <p className="font-mono text-[11px] text-neutral-500 text-center mt-8">No posts yet — follow creators to fill your feed.</p>
        ) : items.map((p) => (
          <div key={p.id} className="border-2 border-[#1A1A1A] bg-[#151515] rounded-xl p-3" data-testid={`cc-feed-item-${p.id}`}>
            <div className="flex items-center gap-2">
              {p.author_avatar
                ? <img src={p.author_avatar} alt="" className="w-6 h-6 rounded-full object-cover border border-[#FFD028]" />
                : <span className="w-6 h-6 rounded-full grid place-items-center bg-[#FFD028] text-black font-black text-[10px]">{(p.author_name || "?")[0]}</span>}
              <span className="font-mono text-[11px] font-bold text-white truncate">{p.author_name || "Creator"}</span>
              {p.author_handle && <span className="font-mono text-[9px] text-neutral-500 truncate">@{p.author_handle}</span>}
            </div>
            {(p.title || p.text) && <p className="mt-1.5 text-[12px] text-neutral-200 leading-snug line-clamp-3">{p.title || p.text}</p>}
            <div className="mt-2 flex items-center gap-3 text-neutral-500">
              <span className="flex items-center gap-1 font-mono text-[10px]"><Heart size={11} /> {p.likes ?? 0}</span>
              <span className="flex items-center gap-1 font-mono text-[10px]"><MessageCircle size={11} /> {p.comments ?? 0}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MusicPanel() {
  const [tab, setTab] = useState("mine"); // mine | community
  const [mine, setMine] = useState(null);
  const [community, setCommunity] = useState(null);
  const [nowId, setNowId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const audioRef = useRef(null);
  const objUrlRef = useRef(null);

  useEffect(() => {
    api.get("/alix/context/vault")
      .then((r) => setMine((r.data?.gallery || []).filter((a) => a.track === "music")))
      .catch(() => setMine([]));
    socialApi.globalFeed?.(0, 25).then((d) => {
      const items = d.items || d.posts || d || [];
      const music = (Array.isArray(items) ? items : []).flatMap((p) =>
        (p.media || []).filter((m) => (m.type || "").startsWith("audio") || (m.kind === "music"))
          .map((m) => ({ asset_id: `${p.id}-${m.url}`, title: p.title || p.text || "Community track", url: m.url, by: p.author_name })));
      setCommunity(music);
    }).catch(() => setCommunity([]));
    return () => { if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current); };
  }, []);

  const play = async (t) => {
    if (nowId === t.asset_id) { audioRef.current?.pause(); setNowId(null); return; }
    setLoadingId(t.asset_id);
    try {
      const path = (t.url || "").replace(/^\/api/, "");
      let src = t.url;
      if (path.startsWith("/")) {
        const r = await api.get(path, { responseType: "blob" });
        if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
        objUrlRef.current = URL.createObjectURL(r.data);
        src = objUrlRef.current;
      }
      if (audioRef.current) { audioRef.current.src = src; await audioRef.current.play(); setNowId(t.asset_id); }
    } catch { toast.error("Couldn't play that track"); }
    finally { setLoadingId(null); }
  };

  const list = tab === "mine" ? mine : community;
  return (
    <div className={`${PANEL} col-start-2 row-start-1`} data-testid="cc-music-panel">
      <div className={HEAD}>
        <Music2 size={14} className="text-[#FFD028]" /><span className={HTITLE}>Music</span>
        <div className="ml-auto flex gap-1">
          {[["mine", Library], ["community", Users]].map(([k, Ic]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`cc-music-tab-${k}`}
              className={`flex items-center gap-1 px-2 py-1 rounded-md border-2 border-[#1A1A1A] font-mono text-[8px] font-black uppercase tracking-widest ${tab === k ? "bg-[#FFD028] text-black" : "bg-[#151515] text-neutral-400"}`}>
              <Ic size={10} /> {k === "mine" ? "Vault" : "Shared"}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-1.5">
        {list === null ? (
          <div className="h-full grid place-items-center"><Loader2 className="animate-spin text-[#FFD028]" size={18} /></div>
        ) : list.length === 0 ? (
          <p className="font-mono text-[11px] text-neutral-500 text-center mt-8">{tab === "mine" ? "Your music vault is empty." : "No community tracks shared yet."}</p>
        ) : list.map((t) => {
          const active = nowId === t.asset_id;
          return (
            <button key={t.asset_id} onClick={() => play(t)} data-testid={`cc-track-${t.asset_id}`}
              className={`w-full flex items-center gap-2.5 border-2 rounded-xl px-3 py-2 text-left ${active ? "border-[#FFD028] bg-[#FFD028]/10" : "border-[#1A1A1A] bg-[#151515]"}`}>
              <span className="w-8 h-8 shrink-0 grid place-items-center rounded-lg bg-[#FFD028] text-black border-2 border-[#1A1A1A]">
                {loadingId === t.asset_id ? <Loader2 size={13} className="animate-spin" /> : active ? <Pause size={13} /> : <Play size={13} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-mono text-[11px] font-bold text-white truncate">{t.title || "Untitled"}</span>
                {t.by && <span className="block font-mono text-[8px] text-neutral-500 truncate">by {t.by}</span>}
              </span>
            </button>
          );
        })}
      </div>
      <audio ref={audioRef} onEnded={() => setNowId(null)} className="hidden" />
    </div>
  );
}

function JobsPanel() {
  const [data, setData] = useState(null);
  const load = () => api.get("/pipelines/my-jobs", { params: { limit: 12 } }).then((r) => setData(r.data)).catch(() => setData({ jobs: [], active: 0 }));
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, []);
  const jobs = data?.jobs || [];
  return (
    <div className={`${PANEL} col-span-2 row-start-2`} data-testid="cc-jobs-panel">
      <div className={HEAD}>
        <Activity size={14} className="text-[#FFD028]" /><span className={HTITLE}>Generations & Status</span>
        {data?.active > 0 && <span className="ml-auto flex items-center gap-1 font-mono text-[9px] font-black uppercase text-[#FFD028]"><Loader2 size={10} className="animate-spin" /> {data.active} active</span>}
      </div>
      <div className="flex-1 overflow-y-auto no-scrollbar p-3">
        {data === null ? (
          <div className="h-full grid place-items-center"><Loader2 className="animate-spin text-[#FFD028]" size={18} /></div>
        ) : jobs.length === 0 ? (
          <p className="font-mono text-[11px] text-neutral-500 text-center mt-6">No generations yet — create something and it'll show here.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {jobs.map((j) => {
              const s = STATUS_UI[j.status] || { c: "text-neutral-400", I: Clock, label: j.status };
              const TrackIcon = j.track === "video" ? Film : j.track === "music" ? Music2 : ImageIcon;
              return (
                <div key={j.job_id} className="flex items-center gap-2 border-2 border-[#1A1A1A] bg-[#151515] rounded-xl px-3 py-2" data-testid={`cc-job-${j.job_id}`}>
                  <TrackIcon size={15} className="text-neutral-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] font-bold text-white truncate uppercase">{(j.kind || "job").replace(/_/g, " ")}</p>
                    <p className="font-mono text-[8px] text-neutral-500 truncate">{j.error || (j.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                  <span className={`flex items-center gap-1 font-mono text-[8px] font-black uppercase ${s.c}`}>
                    <s.I size={11} className={s.spin ? "animate-spin" : ""} /> {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function UserCommandCentre() {
  return (
    <div className="hidden md:grid fixed left-[532px] right-6 top-6 bottom-6 z-10 grid-cols-2 grid-rows-[minmax(0,1fr)_minmax(190px,26%)] gap-4"
      data-testid="user-command-centre">
      <FeedPanel />
      <MusicPanel />
      <JobsPanel />
    </div>
  );
}
